import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CsIconModule } from '@components/icon';
import { NgClass } from '@angular/common';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from '../../../../app.config';
import { EventLoggerService } from '@services/event-logger-service';
import { GamificationDailyTipsComponent } from '../gamification-daily-tips/gamification-daily-tips.component';
import {
  GamificationIntroBottomsheetComponent
} from '@shared/bottom-sheet-layouts/gamification-intro-bottomsheet/gamification-intro-bottomsheet.component';
import {
  GamificationStreakBrokenBottomsheetComponent
} from '@shared/bottom-sheet-layouts/gamification-streak-broken-bottomsheet/gamification-streak-broken-bottomsheet.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { GamificationScratchCardComponent } from '../gamification-scratch-card/gamification-scratch-card.component';
import { ProductUsageTrackingResponseData } from '@cureskin/api-client/src/server-api/api';
import {DayCompletionState, DayStage, PartOfDay} from './gamification-constant';

interface Product {
  category: string;
  name: string;
  productObjectId: string;
  trackingCategoryType: string;
  morning: boolean;
  night: boolean;
  morningFromApi: boolean;
  nightFromApi: boolean;
  hasMorningInstruction: boolean;
  hasNightInstruction: boolean;
}

interface UpdatePayload {
  objectId: string;
  productUsage: Array<{
    productObjectId: string;
    partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT;
    isUsed: boolean;
  }>;
}

interface DayInfo {
  dayNumber: number;
  isUnlocked: boolean;
  isCompleted: boolean;
  isSelected: boolean;
  isToday: boolean;
  date?: string;
  data?: any;
}

@Component({
  selector: 'app-gamification-banner',
  imports: [
    CsIconModule,
    NgClass,
    GamificationDailyTipsComponent,
    GamificationScratchCardComponent
],
  templateUrl: './gamification-banner.component.html',
  standalone: true
})
export class GamificationBannerComponent implements OnInit {
  @Input() regimen: { objectId: string };
  @Input() user: Table.User;
  @Input() todayProductUsageData: ProductUsageTrackingResponseData;
  @Output() scratchCardDismissedWithoutClaim = new EventEmitter<void>();
  @Output() rewardClaimed = new EventEmitter<any>();

  private scrollToBanner(): void {
    setTimeout(() => {
      const bannerElement = document.querySelector('app-gamification-banner');
      if (bannerElement) {
        bannerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }
  username: string;
  isExpanded = false;
  sevenDaysData: DayInfo[] = [];
  selectedDayIndex: number = 0;
  selectedDayProducts: Product[] = [];
  selectedDayTip: string | null = null;
  bannerDay: number = 1;
  todayDate: Date;
  updatePayload: UpdatePayload = {
    objectId: '',
    productUsage: []
  };
  isSaved: boolean = false;
  showScratchCard: boolean = false;
  rewardData: { objectId: string; amount?: number; expireAt?: { iso?: string } } | null = null;
  isRewardClaimed: boolean = false;
  claimedRewardAmount: number | null = null;
  pendingScratchCardReward: any = null;

  constructor(
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly bottomSheet: MatBottomSheet,
    private readonly eventLogger: EventLoggerService
  ) {}

  async ngOnInit(): Promise<void> {
    this.username = this.user?.get('PatientName')?.split(' ')[0];
    await this.initializeGamification();
  }

  async initializeGamification(): Promise<void> {
    const todayData = this.todayProductUsageData;
    this.bannerDay = todayData?.bannerDay;

    if (todayData?.date?.iso) {
      this.todayDate = new Date(todayData.date.iso);
    } else {
      this.todayDate = new Date();
    }

    if (todayData.daysSinceDelivery <= 2) this.isExpanded = true;

    this.clearStreakBrokenLocalStorage(todayData?.streak);
    await this.checkAndShowIntroBottomsheet(todayData?.introBottomsheetShown, todayData.objectId);
    this.checkAndShowStreakBrokenBottomSheet(todayData);
    this.initializeSevenDaysData(todayData);
    await this.selectDay(this.bannerDay - 1, false);
  }

  initializeSevenDaysData(todayData: any): void {
    this.sevenDaysData = [];

    for (let i = 0; i < 7; i++) {
      const dayNumber = i + 1;
      const isUnlocked = dayNumber <= this.bannerDay;
      const isToday = dayNumber === this.bannerDay;

      const daysOffset = dayNumber - this.bannerDay;
      const dayDate = new Date(this.todayDate);
      dayDate.setDate(this.todayDate.getDate() + daysOffset);
      const dateString = dayDate.toISOString().split('T')[0];
      let isCompleted = false;
      if (dayNumber < this.bannerDay) {
        isCompleted = true;
      } else if (dayNumber === this.bannerDay) {
        isCompleted = todayData?.dayStage === DayStage.COMPLETE;
      }

      const dayInfo: DayInfo = {
        dayNumber,
        isUnlocked,
        isCompleted,
        isSelected: false,
        isToday,
        date: dateString,
        data: isToday ? todayData : undefined
      };

      this.sevenDaysData.push(dayInfo);
    }
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'skinStart_expand' }));
    } else {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'skinStart_collapse' }));
    }
  }

  async selectDay(dayIndex: number, isUserInitiated: boolean = true): Promise<void> {
    if (!this.sevenDaysData[dayIndex]?.isUnlocked) {
      return;
    }

    if (isUserInitiated) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'skinStart_day_change' }));
    }

    const selectedDay = this.sevenDaysData[dayIndex];

    if (selectedDay.data) {
      await this.handleDayData(selectedDay.data, dayIndex);
      return;
    }

    if (!selectedDay.date || !this.regimen?.objectId) {
      return;
    }

    try {
      const dayData = await this.conn.getProductUsage(this.regimen.objectId, selectedDay.date);

      selectedDay.data = dayData;
      selectedDay.isCompleted = dayData?.dayStage === DayStage.COMPLETE;

      await this.handleDayData(dayData, dayIndex);
    } catch (error) {
      console.error(`Error fetching data for day ${selectedDay.dayNumber}:`, error);
      this.setSelectedDay(dayIndex);
    }
  }

  private async handleDayData(dayData: any, dayIndex: number): Promise<void> {
    this.selectedDayTip = null;
    this.showScratchCard = false;
    this.rewardData = null;
    this.isRewardClaimed = false;
    this.claimedRewardAmount = null;

    if (this.bannerDay === 7 && dayData?.dayStage === DayStage.COMPLETE && this.hasScratchCardReward(dayData)) {
      await this.processScratchCardReward(dayData.scratchCardReward);
    } else if (this.hasTip(dayData)) {
      this.selectedDayTip = await this.fetchTipText(dayData.tipLanguageString.objectId);
    }

    this.setSelectedDay(dayIndex);
  }

  private setSelectedDay(dayIndex: number): void {
    this.sevenDaysData.forEach(day => day.isSelected = false);

    this.sevenDaysData[dayIndex].isSelected = true;
    this.selectedDayIndex = dayIndex;

    this.updateSelectedDayProducts();
  }

  hasTip(dayData: any): boolean {
    return !!dayData?.tipLanguageString?.objectId;
  }

  hasScratchCardReward(dayData: any): boolean {
    return !!dayData?.scratchCardReward?.objectId;
  }

  async processScratchCardReward(scratchCardReward: any): Promise<void> {
    if (!scratchCardReward?.objectId) {
      return;
    }

    const rewards = await this.conn.fetchRewards({ user: this.user });

    const reward = rewards?.find((singleReward: any) => {
      const rewardId = singleReward.objectId || singleReward.get?.('objectId') || singleReward.id;
      return rewardId === scratchCardReward.objectId;
    });

    if (!reward) return;

    const cashTransaction = reward.get?.('cashTransaction') || reward.cashTransaction;
    const amount = cashTransaction?.amount || cashTransaction?.get?.('amount') || reward.get?.('amount') || reward.amount;
    const isClaimed = reward.get?.('isClaimed') || reward.isClaimed;
    const expireAt = reward.get?.('expireAt') || reward.expireAt;

    if (isClaimed) {
      this.isRewardClaimed = true;
      this.claimedRewardAmount = amount;
      return;
    }

    const rewardId = reward.objectId || reward.get?.('objectId') || reward.id;

    this.rewardData = {
      objectId: rewardId,
      amount: amount,
      expireAt: expireAt
    };

    this.showScratchCard = true;
  }

  async fetchTipText(tipLanguageStringObjectId: string): Promise<string> {
    try {
      const userLanguage = this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;
      const [languageString] = await this.conn.getLanguageStringsById([tipLanguageStringObjectId]);
      return languageString.get(userLanguage) || languageString.get(this.appConfig.Shared.Languages.EN);
    } catch (error) {
      console.error('Error fetching tip text:', error);
      return '';
    }
  }

  shouldShowTip(): boolean {
    return !!this.selectedDayTip;
  }

  shouldShowRewardClaimed(): boolean {
    return this.isRewardClaimed && this.bannerDay === 7;
  }

  shouldShowProductUsage(): boolean {
    return !this.selectedDayTip && !this.isRewardClaimed && this.selectedDayProducts.length > 0;
  }

  updateSelectedDayProducts(): void {
    const selectedDay = this.sevenDaysData[this.selectedDayIndex];

    if (this.shouldClearProducts(selectedDay)) {
      this.selectedDayProducts = [];
      this.resetUpdatePayload();
      return;
    }

    this.updatePayloadObjectId(selectedDay?.data?.objectId);
    this.selectedDayProducts = selectedDay.data.products.map((apiProduct: any) =>
      this.mapApiProductToProduct(apiProduct)
    );
  }

  private shouldClearProducts(selectedDay: DayInfo | undefined): boolean {
    return !!this.selectedDayTip || this.isRewardClaimed || !selectedDay?.data?.products || selectedDay.data.products.length === 0;
  }

  private updatePayloadObjectId(objectId: string | undefined): void {
    if (!objectId) {
      return;
    }

    const previousObjectId = this.updatePayload.objectId;

    this.updatePayload.objectId = objectId;
    if (objectId === previousObjectId) return
    this.resetUpdatePayload();
  }

  private processProductInstructions(instructions: any[]): {
    morning: boolean;
    night: boolean;
    morningFromApi: boolean;
    nightFromApi: boolean;
    hasMorningInstruction: boolean;
    hasNightInstruction: boolean;
  } {
    let morning = false;
    let night = false;
    let morningFromApi = false;
    let nightFromApi = false;
    let hasMorningInstruction = false;
    let hasNightInstruction = false;

    instructions.forEach((instruction: any) => {
      if (instruction.partOfDay === PartOfDay.MORNING) {
        hasMorningInstruction = true;
        if (instruction.isUsed) {
          morning = true;
          morningFromApi = true;
        }
      }
      if (instruction.partOfDay === PartOfDay.NIGHT) {
        hasNightInstruction = true;
        if (instruction.isUsed) {
          night = true;
          nightFromApi = true;
        }
      }
    });

    return {
      morning,
      night,
      morningFromApi,
      nightFromApi,
      hasMorningInstruction,
      hasNightInstruction
    };
  }

  private mapApiProductToProduct(apiProduct: any): Product {
    const instructions = apiProduct.instructions && Array.isArray(apiProduct.instructions)
      ? apiProduct.instructions
      : [];

    const instructionState = this.processProductInstructions(instructions);

    return {
      category: apiProduct.purpose || apiProduct.trackingCategoryType,
      name: apiProduct.productTitle,
      productObjectId: apiProduct.objectId,
      trackingCategoryType: apiProduct.purpose || apiProduct.trackingCategoryType ,
      ...instructionState
    };
  }

  resetUpdatePayload(): void {
    const currentObjectId = this.updatePayload.objectId;
    this.updatePayload = {
      objectId: currentObjectId || '',
      productUsage: []
    };
  }

  toggleInfo(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'skinStart_info_icon' }));
    const isSmallScreen = window.screen.height < 700;
    this.bottomSheet.open(GamificationIntroBottomsheetComponent, {
      panelClass: isSmallScreen ? 'bottomsheet-fixed-height-85vh' : '',
      data: {
        languagePreference: this.user?.get('languagePreference') ?? this.appConfig.Shared.Languages.EN,
      }
    });
  }

  private async checkAndShowIntroBottomsheet(
    previousDayBottomsheetShown: boolean,
    objectId: string,
  ) {
    if (previousDayBottomsheetShown) return;

    const isSmallScreen = window.screen.height < 700;
    this.bottomSheet.open(GamificationIntroBottomsheetComponent, {
      panelClass: isSmallScreen ? 'bottomsheet-fixed-height-85vh' : '',
      data: {
        onClose: () => this.scrollToBanner(),
        languagePreference: this.user?.get('languagePreference') ?? this.appConfig.Shared.Languages.EN,
      }
    });
    await this.conn.updateProductUsage(objectId, undefined, true);
    const today = new Date();
    const todayDateString = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getFullYear()).slice(-2)}`;
    localStorage.setItem('gamificationBottomSheetShown', todayDateString);
  }

  private checkAndShowStreakBrokenBottomSheet(todayData: ProductUsageTrackingResponseData): void {
    if (!todayData.introBottomsheetShown) return;

    const streak = todayData?.streak ?? 0;
    const daysSinceDelivery = todayData?.daysSinceDelivery ?? 0;
    const isStreakBroken = streak === 0 && this.bannerDay === 1 && daysSinceDelivery > 1;

    if (!isStreakBroken) return;

    const today = new Date();
    const todayDateString = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getFullYear()).slice(-2)}`;
    const storedDate = localStorage.getItem('gamificationBottomSheetShown');

    if (storedDate === todayDateString) return;

    localStorage.setItem('gamificationBottomSheetShown', todayDateString);
    const isSmallScreen = window.screen.height < 700;
    this.bottomSheet.open(GamificationStreakBrokenBottomsheetComponent, {
      panelClass: isSmallScreen ? 'bottomsheet-fixed-height-85vh' : '',
      data: {
        onClose: () => this.scrollToBanner()
      }
    });
  }

  private clearStreakBrokenLocalStorage(streak: number): void {
    if (streak > 1 && this.bannerDay > 1) {
      localStorage.removeItem('gamificationBottomSheetShown');
    }
  }

  isDaytime(): boolean {
    const now = new Date();
    const currentHour = now.getHours();
    return currentHour >= 6 && currentHour < 19;
  }

  isNightCheckboxDisabled(productIndex: number): boolean {
    return this.isDaytime();
  }

  toggleCheckbox(productIndex: number, partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT): void {
    const product = this.selectedDayProducts[productIndex];
    if (!product) return;

    if (partOfDay === PartOfDay.NIGHT && this.isNightCheckboxDisabled(productIndex)) {
      return;
    }

    if (partOfDay === PartOfDay.MORNING && !product.hasMorningInstruction) {
      return;
    }
    if (partOfDay === PartOfDay.NIGHT && !product.hasNightInstruction) {
      return;
    }

    if (partOfDay === PartOfDay.MORNING && product.morningFromApi) {
      return;
    }
    if (partOfDay === PartOfDay.NIGHT && product.nightFromApi) {
      return;
    }

    const isCurrentlyChecked = partOfDay === PartOfDay.MORNING ? product.morning : product.night;
    const newState = !isCurrentlyChecked;

    if (partOfDay === PartOfDay.MORNING) {
      product.morning = newState;
    } else {
      product.night = newState;
    }

    this.updatePayloadInProductUsage(product.productObjectId, partOfDay, newState);
  }

  private updatePayloadInProductUsage(productObjectId: string, partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT, isUsed: boolean): void {
    const existingIndex = this.updatePayload.productUsage.findIndex(
      item => item.productObjectId === productObjectId && item.partOfDay === partOfDay
    );

    if (isUsed) {
      if (existingIndex >= 0) {
        this.updatePayload.productUsage[existingIndex].isUsed = true;
      } else {
        this.updatePayload.productUsage.push({
          productObjectId,
          partOfDay,
          isUsed: true
        });
      }
    } else if (existingIndex >= 0) {
      this.updatePayload.productUsage.splice(existingIndex, 1);
    }
  }

  isCheckboxChecked(productIndex: number, partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT): boolean {
    const product = this.selectedDayProducts[productIndex];
    if (!product) return false;
    return partOfDay === PartOfDay.MORNING ? product.morning : product.night;
  }

  isCheckboxFromApi(productIndex: number, partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT): boolean {
    const product = this.selectedDayProducts[productIndex];
    if (!product) return false;
    return partOfDay === PartOfDay.MORNING ? product.morningFromApi : product.nightFromApi;
  }

  isCheckboxCheckedByUser(productIndex: number, partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT): boolean {
    const product = this.selectedDayProducts[productIndex];
    if (!product) return false;

    const isChecked = partOfDay === PartOfDay.MORNING ? product.morning : product.night;
    const isFromApi = partOfDay === PartOfDay.MORNING ? product.morningFromApi : product.nightFromApi;

    return isChecked && !isFromApi;
  }

  shouldShowSaveButton(): boolean {
    return this.updatePayload.productUsage.length > 0;
  }

  async saveUsage(): Promise<void> {
    if (!this.canSaveUsage()) {
      return;
    }

    const updatedData = await this.conn.updateProductUsage(
      this.updatePayload.objectId,
      this.updatePayload.productUsage
    );

    this.logSaveUsageEvents();
    const pendingTip = await this.processUpdatedData(updatedData);
    this.handlePostSaveAnimation(pendingTip);
  }

  private canSaveUsage(): boolean {
    return !!(this.updatePayload.objectId && this.updatePayload.productUsage.length > 0);
  }

  private async processUpdatedData(updatedData: any): Promise<string | null> {
    let pendingTip: string | null = null;
    this.pendingScratchCardReward = null;

    const selectedDay = this.sevenDaysData[this.selectedDayIndex];
    if (selectedDay) {
      selectedDay.data = updatedData;
      selectedDay.isCompleted = updatedData?.dayStage === DayStage.COMPLETE;

      if (this.shouldShowScratchCardReward(updatedData)) {
        this.pendingScratchCardReward = updatedData.scratchCardReward;
      } else if (this.hasTip(updatedData)) {
        pendingTip = await this.fetchTipText(updatedData.tipLanguageString.objectId);
      }

      this.updateSelectedDayProducts();
    }

    return pendingTip;
  }

  private shouldShowScratchCardReward(updatedData: any): boolean {
    return this.bannerDay === 7 && updatedData?.dayStage === DayStage.COMPLETE && this.hasScratchCardReward(updatedData);
  }

  private handlePostSaveAnimation(pendingTip: string | null): void {
    this.isSaved = true;

    setTimeout(() => {
      this.resetUpdatePayload();
      setTimeout(() => {
        this.isSaved = false;

        if (pendingTip) {
          this.selectedDayTip = pendingTip;
        }

        if (this.pendingScratchCardReward) {
          this.processScratchCardReward(this.pendingScratchCardReward);
          this.pendingScratchCardReward = null;
        }
      }, 100);
    }, 3000);
  }

  onScratchCardClosed(): void {
    if (!this.isRewardClaimed && this.rewardData) {
      this.scratchCardDismissedWithoutClaim.emit();
    }
    const wasClaimed = this.isRewardClaimed;
    this.showScratchCard = false;
    this.rewardData = null;
    if (wasClaimed) {
      this.rewardClaimed.emit({ refreshBanner: true, objectId: null, amount: this.claimedRewardAmount });
    }
  }

  onRewardClaimed(reward: any): void {
    this.isRewardClaimed = true;
    this.claimedRewardAmount = reward.amount;

    const selectedDay = this.sevenDaysData[this.selectedDayIndex];
    if (selectedDay.data) {
      if (selectedDay.data.scratchCardReward) {
        selectedDay.data.scratchCardReward.isClaimed = true;
      }
    }

    this.rewardClaimed.emit(reward);
  }

  private logSaveUsageEvents(): void {
    const selectedDay = this.sevenDaysData[this.selectedDayIndex];
    if (!selectedDay) {
      return;
    }

    const dayNumber = selectedDay.dayNumber;
    const updatedPartsOfDay = new Set(
      this.updatePayload.productUsage.map(item => item.partOfDay)
    );

    if (updatedPartsOfDay.has(PartOfDay.MORNING)) {
      const completionState = this.getCompletionState(PartOfDay.MORNING);
      this.eventLogger.cleverTapEvent('click', JSON.stringify({
          name: `skinStart_day_${dayNumber}_morning_saved`,
          completion_state: completionState
        })
      );
    }

    if (updatedPartsOfDay.has(PartOfDay.NIGHT)) {
      const completionState = this.getCompletionState(PartOfDay.NIGHT);
      this.eventLogger.cleverTapEvent('click', JSON.stringify({
          name: `skinStart_day_${dayNumber}_night_saved`,
          completion_state: completionState
        })
      );
    }
  }

  private getCompletionState(partOfDay: PartOfDay.MORNING | PartOfDay.NIGHT): DayCompletionState.FULL | DayCompletionState.PARTIAL {
    const relevantProducts = this.selectedDayProducts.filter(product => {
      return partOfDay === PartOfDay.MORNING ? product.hasMorningInstruction : product.hasNightInstruction;
    });

    if (relevantProducts.length === 0) return DayCompletionState.PARTIAL;

    const allUsed = relevantProducts.every(product => {
      const updatedProduct = this.updatePayload.productUsage.find(
        item => item.productObjectId === product.productObjectId && item.partOfDay === partOfDay
      );

      if (updatedProduct) {
        return updatedProduct.isUsed;
      }

      return partOfDay === PartOfDay.MORNING ? product.morning : product.night;
    });

    return allUsed ? DayCompletionState.FULL : DayCompletionState.PARTIAL;
  }

  protected readonly PartOfDay = PartOfDay;
}
